import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { templateApi } from '../../api'
import type { ApiError } from '../../api/client'
import type { TrainingSetTemplate } from '../../types/api'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { parseDuration } from '../../utils/duration'
import { getDayAbbreviation, formatDurationTranslated } from '../../utils/dateTranslations'
import { Button, Card } from '../ui'
import { useSubscription } from '../../hooks/useSubscription'

export default function TrainingTemplates() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { canAddTemplate } = useSubscription()

  const [templates, setTemplates] = useState<TrainingSetTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadTemplates()
  }, [])

  async function loadTemplates() {
    setLoading(true)
    setError(null)
    try {
      const data = await templateApi.getTemplates()
      setTemplates(data)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  async function handleCopy(id: number, name: string) {
    const confirmed = confirm(
      (t.trainingTemplates?.copyConfirm || 'Copy template "{{name}}"?').replace('{{name}}', name)
    )
    if (!confirmed) return
    if (!canAddTemplate) {
      navigate('/subscription')
      return
    }
    try {
      const source = await templateApi.getTemplate(id)
      const copy = await templateApi.createTemplate({
        name: `${t.trainingTemplates?.copyPrefix || 'Copy of'} ${source.name}`,
        description: source.description,
        defaultStartTime: source.defaultStartTime,
        plannedTrainings: source.plannedTrainings,
        duration: source.duration,
        maxSkipCount: source.maxSkipCount,
        days: source.days?.map(d => ({ day: d.day, startTime: d.startTime })),
      })
      setTemplates(prev => [copy, ...prev])
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingTemplates?.copyFailed || 'Failed to copy template')
    }
  }

  async function handleDelete(id: number, name: string) {
    const confirmed = confirm(t.trainingTemplates?.confirmDelete || `Delete template "${name}"?`)
    if (!confirmed) return
    try {
      await templateApi.deleteTemplate(id)
      setTemplates(prev => prev.filter(tmpl => tmpl.id !== id))
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || 'Failed to delete template')
    }
  }

  return (
    <div className="p-5 pb-24">
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.trainingTemplates.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.trainingTemplates.subtitle}
        </p>
      </header>

      <Button
        type="button"
        variant="primary"
        size="lg"
        fullWidth
        className="mb-6"
        onClick={() => navigate(canAddTemplate ? '/templates/create' : '/subscription')}
      >
        + {t.trainingTemplates.createNew}
      </Button>

      {error && (
        <div
          className="rounded-xl p-4 mb-4 flex items-start gap-3"
          style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5" style={{ color: '#ef4444' }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-sm" style={{ color: theme.text.muted }}>
          {t.common.loading}
        </div>
      ) : templates.length > 0 ? (
        <div className="flex flex-col gap-3">
          {templates.map((template) => {
            const chips: string[] = []
            if (template.plannedTrainings) {
              chips.push(`${template.plannedTrainings} ${t.trainingTemplates.trainings}`)
            }
            if (template.duration) {
              chips.push(formatDurationTranslated(parseDuration(template.duration), t))
            }
            if (template.days && template.days.length > 0) {
              chips.push(template.days.map(d => getDayAbbreviation(d.day, t)).join(' · '))
            }
            if (template.defaultStartTime) {
              chips.push(template.defaultStartTime.slice(0, 5))
            }
            if (template.maxSkipCount != null) {
              chips.push(`${t.trainingTemplates.maxSkip}: ${template.maxSkipCount}`)
            }

            return (
              <Card key={template.id} className="overflow-hidden">
                <div>
                  <p className="text-base font-bold" style={{ color: theme.text.primary }}>
                    {template.name}
                  </p>
                  {template.description && (
                    <p className="text-sm mt-0.5" style={{ color: theme.text.muted }}>
                      {template.description}
                    </p>
                  )}
                </div>

                {chips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {chips.map((chip, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-medium px-2.5 py-1 rounded-full"
                        style={{
                          backgroundColor: theme.cardBg,
                          border: `1px solid ${theme.cardBorder}`,
                          color: theme.text.muted,
                        }}
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                )}

                <div
                  className="-mx-4 -mb-4 mt-1 flex items-center justify-between px-3 py-2"
                  style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                >
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      title={t.trainingTemplates.edit}
                      onClick={() => navigate(`/templates/edit/${template.id}`)}
                      className="p-3 rounded-xl transition-colors"
                      style={{ color: theme.accent.text }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      title={t.trainingTemplates.copy}
                      onClick={() => handleCopy(template.id, template.name)}
                      className="p-3 rounded-xl transition-opacity"
                      style={{ color: theme.text.muted }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    </button>
                  </div>
                  <button
                    type="button"
                    title={t.trainingTemplates.delete}
                    onClick={() => handleDelete(template.id, template.name)}
                    className="p-3 rounded-xl transition-colors"
                    style={{ color: '#ef4444' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6"/><path d="M14 11v6"/>
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </div>
              </Card>
            )
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center text-center py-12 px-6">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.text.muted }}>
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
          </div>
          <h3 className="text-lg font-bold mb-1" style={{ color: theme.text.primary }}>
            {t.trainingTemplates.empty.title}
          </h3>
          <p className="text-sm mb-6" style={{ color: theme.text.muted }}>
            {t.trainingTemplates.empty.description}
          </p>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => navigate(canAddTemplate ? '/templates/create' : '/subscription')}
          >
            + {t.trainingTemplates.createNew}
          </Button>
        </div>
      )}
    </div>
  )
}
